import { Plus } from 'lucide-react';
import { observer } from 'mobx-react';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@components/page-header';
import { Select } from '@components/select';
import {
    CellProps,
    ReactColumn,
    SortingRule,
    TableComponent
} from '@components/table';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import {
    ActivityPointsMultiplierTierGroupDto,
    ActivityType,
    WorkoutCategory,
    WorkoutCategoryWithTypesDto,
    WorkoutTypeV2
} from '../../core/api/FitnessActivityClient';
import {
    ActivityPointsRulesFilters,
    useActivityPointsRulesStore
} from './list-store';
import { WorkoutRecordingMethod } from './recording-method.types';
import { links } from '../../core/router';
import { DeleteActivityPointsRuleDialog } from './delete-activity-points-rule-dialog';
import { Api } from '../../core/api';
import { injectTSDI } from '../../core/tsdi';
import { TierPreview } from './tier-preview';

function formatEnumLabel(value?: string | null): string {
    if (!value) {
        return '-';
    }

    return value
        .toLowerCase()
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

function hasRequirementFilters(filters: ActivityPointsRulesFilters): boolean {
    return (
        filters.activityType !== undefined ||
        filters.workoutCategory !== undefined ||
        filters.workoutType !== undefined ||
        filters.recordingMethod !== undefined ||
        filters.requiresAverageSpeed !== undefined ||
        filters.requiresCaloriesPerMinute !== undefined
    );
}

function activityTypeBadgeClass(activityType: ActivityType): string {
    switch (activityType) {
        case ActivityType.WORKOUT:
            return 'border-chart-1 text-chart-1';
        case ActivityType.FACILITY_VISIT:
            return 'border-chart-2 text-chart-2';
        case ActivityType.DAILY_STEPS:
        default:
            return 'border-primary text-primary';
    }
}

const enumOptions = (values: string[], allLabel: string) => [
    { label: allLabel, value: '' },
    ...values.map((value) => ({ label: formatEnumLabel(value), value }))
];

const REQUIREMENT_FILTER_OPTIONS = [
    { label: 'All', value: '' },
    { label: 'Required', value: 'required' },
    { label: 'Not Required', value: 'not_required' }
];

const requirementFilterValue = (value?: boolean) =>
    value === true ? 'required' : value === false ? 'not_required' : '';

export const WorkoutRulesActivityPointsRules = observer(() => {
    const navigate = useNavigate();
    const { fitnessActivityClient } = injectTSDI(Api);
    const {
        activityPointsRules,
        sorting,
        setSorting,
        setSearchTerm,
        dispatch,
        searchParams,
        filters,
        updateFilters,
        clearFilters
    } = useActivityPointsRulesStore();

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedRule, setSelectedRule] =
        useState<ActivityPointsMultiplierTierGroupDto | null>(null);
    const [workoutCategoriesWithTypes, setWorkoutCategoriesWithTypes] =
        useState<WorkoutCategoryWithTypesDto[]>([]);

    React.useEffect(() => {
        let active = true;

        const fetchWorkoutCategories = async () => {
            try {
                const response =
                    await fitnessActivityClient.admin.workoutCategoryAdminControllerGetWorkoutCategories();

                if (active) {
                    setWorkoutCategoriesWithTypes(response.data || []);
                }
            } catch (error) {
                // keep default enum fallbacks when endpoint fails
                if (active) {
                    setWorkoutCategoriesWithTypes([]);
                }
            }
        };

        void fetchWorkoutCategories();

        return () => {
            active = false;
        };
    }, [fitnessActivityClient]);

    const workoutCategories = React.useMemo(() => {
        if (workoutCategoriesWithTypes.length > 0) {
            return workoutCategoriesWithTypes.map((item) => item.category);
        }

        return Object.values(WorkoutCategory);
    }, [workoutCategoriesWithTypes]);

    const workoutTypesByCategory = React.useMemo(
        () =>
            workoutCategoriesWithTypes.reduce(
                (acc, current) => {
                    acc[current.category] = current.types || [];
                    return acc;
                },
                {} as Record<WorkoutCategory, WorkoutTypeV2[]>
            ),
        [workoutCategoriesWithTypes]
    );

    const workoutTypeOptions = React.useMemo(() => {
        if (filters.workoutCategory) {
            const categoryTypes =
                workoutTypesByCategory[filters.workoutCategory];
            if (categoryTypes && categoryTypes.length > 0) {
                return categoryTypes;
            }
            return Object.values(WorkoutTypeV2);
        }

        if (workoutCategoriesWithTypes.length > 0) {
            return Array.from(
                new Set(
                    workoutCategoriesWithTypes.flatMap(
                        (item) => item.types || []
                    )
                )
            );
        }

        return Object.values(WorkoutTypeV2);
    }, [
        filters.workoutCategory,
        workoutCategoriesWithTypes,
        workoutTypesByCategory
    ]);

    const onSortingChange = (nextSorting?: SortingRule) => {
        if (nextSorting) {
            setSorting(nextSorting);
        }
    };

    const workoutScopeDisabled =
        filters.activityType !== undefined &&
        filters.activityType !== ActivityType.WORKOUT;
    const totalRules = activityPointsRules.$?.totalElements || 0;

    const setRequiredAverageSpeedFilter = (value: string) => {
        updateFilters({
            requiresAverageSpeed:
                value === 'required'
                    ? true
                    : value === 'not_required'
                      ? false
                      : undefined
        });
    };

    const setRequiredCaloriesFilter = (value: string) => {
        updateFilters({
            requiresCaloriesPerMinute:
                value === 'required'
                    ? true
                    : value === 'not_required'
                      ? false
                      : undefined
        });
    };

    const onActivityTypeFilterChange = (value: ActivityType | '') => {
        if (!value) {
            updateFilters({
                activityType: undefined,
                workoutCategory: undefined,
                workoutType: undefined,
                recordingMethod: undefined
            });
            return;
        }

        if (value !== ActivityType.WORKOUT) {
            updateFilters({
                activityType: value,
                workoutCategory: undefined,
                workoutType: undefined,
                recordingMethod: undefined
            });
            return;
        }

        updateFilters({ activityType: value });
    };

    const columns: ReactColumn<ActivityPointsMultiplierTierGroupDto>[] = [
        {
            Header: 'Scope',
            accessor: 'activityType',
            style: {
                width: '44%',
                minWidth: 280,
                whiteSpace: 'normal',
                textOverflow: 'clip',
                overflow: 'visible'
            },
            Cell: ({
                row: {
                    original: { activityType, workoutCategory, workoutType }
                }
            }: CellProps<
                ActivityPointsMultiplierTierGroupDto,
                ActivityType
            >) => {
                const categoryLabel = workoutCategory
                    ? formatEnumLabel(workoutCategory)
                    : 'All categories';
                const typeLabel = workoutType
                    ? formatEnumLabel(workoutType)
                    : 'All types';
                const scopeSummary =
                    activityType === ActivityType.WORKOUT
                        ? `${categoryLabel} / ${typeLabel}`
                        : 'No workout scope';

                return (
                    <div className="flex flex-col gap-1">
                        <div>
                            <Badge
                                variant="outline"
                                className={activityTypeBadgeClass(activityType)}
                            >
                                {formatEnumLabel(activityType)}
                            </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">
                            {scopeSummary}
                        </p>
                    </div>
                );
            }
        },
        {
            Header: 'Requirements',
            id: 'requirements',
            disableSortBy: true,
            style: {
                width: '46%',
                minWidth: 380,
                whiteSpace: 'normal',
                textOverflow: 'clip',
                overflow: 'visible'
            },
            Cell: ({
                row: {
                    original: {
                        requiredAverageSpeed,
                        requiredCaloriesPerMinute,
                        recordingMethod
                    }
                }
            }: CellProps<
                ActivityPointsMultiplierTierGroupDto,
                number | undefined
            >) => {
                const requirementChips: {
                    label: string;
                    variant: 'info' | 'secondary';
                }[] = [];

                if (
                    requiredAverageSpeed !== undefined &&
                    requiredAverageSpeed !== null
                ) {
                    requirementChips.push({
                        label: `Avg speed >= ${requiredAverageSpeed.toFixed(2)} km/h`,
                        variant: 'info'
                    });
                }

                if (
                    requiredCaloriesPerMinute !== undefined &&
                    requiredCaloriesPerMinute !== null
                ) {
                    requirementChips.push({
                        label: `Calories/min >= ${requiredCaloriesPerMinute.toFixed(2)}`,
                        variant: 'info'
                    });
                }

                if (recordingMethod) {
                    requirementChips.push({
                        label: `Recording: ${formatEnumLabel(recordingMethod)}`,
                        variant: 'secondary'
                    });
                }

                if (requirementChips.length === 0) {
                    return <Badge variant="muted">None</Badge>;
                }

                return (
                    <div className="flex flex-wrap gap-1.5">
                        {requirementChips.map(({ label, variant }) => (
                            <Badge key={label} variant={variant}>
                                {label}
                            </Badge>
                        ))}
                    </div>
                );
            }
        },
        {
            Header: 'Priority',
            accessor: 'priority',
            style: {
                width: '10%',
                minWidth: 110
            },
            Cell: ({
                row: {
                    original: { priority }
                }
            }: CellProps<ActivityPointsMultiplierTierGroupDto, number>) => (
                <span className="text-sm">{priority}</span>
            )
        },
        {
            Header: '',
            id: 'actions',
            disableSortBy: true,
            width: 50,
            Cell: () => null
        }
    ];

    return (
        <>
            <PageHeader
                title="Activity Points Multiplier Tier Groups"
                description="Manage multiplier scopes and requirements at scale."
                badges={<Badge variant="outline">{totalRules} rules</Badge>}
                actions={
                    <Button
                        onClick={() =>
                            navigate(links.createActivityPointsRule())
                        }
                    >
                        <Plus />
                        Create
                    </Button>
                }
            />

            <div className="mb-4 flex flex-wrap items-end gap-3">
                <div className="min-w-[180px] flex-1">
                    <Select
                        label="Activity Type"
                        value={filters.activityType || ''}
                        options={enumOptions(
                            Object.values(ActivityType),
                            'All'
                        )}
                        onChange={onActivityTypeFilterChange}
                    />
                </div>
                <div className="min-w-[180px] flex-1">
                    <Select
                        label="Workout Category"
                        value={filters.workoutCategory || ''}
                        options={enumOptions(workoutCategories, 'All')}
                        disabled={workoutScopeDisabled}
                        onChange={(value) =>
                            updateFilters({
                                workoutCategory: value || undefined,
                                workoutType: undefined
                            })
                        }
                    />
                </div>
                <div className="min-w-[180px] flex-1">
                    <Select
                        label="Workout Type"
                        value={filters.workoutType || ''}
                        options={enumOptions(workoutTypeOptions, 'All')}
                        disabled={workoutScopeDisabled}
                        onChange={(value) =>
                            updateFilters({ workoutType: value || undefined })
                        }
                    />
                </div>
                <div className="min-w-[180px] flex-1">
                    <Select
                        label="Recording Method"
                        value={filters.recordingMethod || ''}
                        options={enumOptions(
                            Object.values(WorkoutRecordingMethod),
                            'All'
                        )}
                        disabled={workoutScopeDisabled}
                        onChange={(value) =>
                            updateFilters({
                                recordingMethod: value || undefined
                            })
                        }
                    />
                </div>
                <div className="min-w-[160px] flex-1">
                    <Select
                        label="Avg Speed Req"
                        value={requirementFilterValue(
                            filters.requiresAverageSpeed
                        )}
                        options={REQUIREMENT_FILTER_OPTIONS}
                        onChange={setRequiredAverageSpeedFilter}
                    />
                </div>
                <div className="min-w-[160px] flex-1">
                    <Select
                        label="Calories/Min Req"
                        value={requirementFilterValue(
                            filters.requiresCaloriesPerMinute
                        )}
                        options={REQUIREMENT_FILTER_OPTIONS}
                        onChange={setRequiredCaloriesFilter}
                    />
                </div>
                <Button
                    variant="outline"
                    onClick={() => clearFilters()}
                    disabled={!hasRequirementFilters(filters)}
                >
                    Clear
                </Button>
            </div>

            <TableComponent
                columns={columns}
                items={activityPointsRules}
                tableLayout="auto"
                cellPadding={12}
                renderRowSubComponent={TierPreview}
                onRowClick={(rule) =>
                    navigate(links.activityPointsRuleDetails(String(rule.id)))
                }
                onSortingChange={onSortingChange}
                sorting={sorting as SortingRule | undefined}
                onSearchChange={setSearchTerm}
                searchPlaceholder="Search by workout type, category or activity type..."
                onChangePage={(page) => dispatch({ type: 'page', page })}
                onPageSizeChange={(size) => dispatch({ type: 'size', size })}
                contextMenu={[
                    {
                        label: 'View Details',
                        action: (rule) => {
                            navigate(
                                links.activityPointsRuleDetails(String(rule.id))
                            );
                        }
                    },
                    {
                        label: 'Edit',
                        action: (rule) => {
                            navigate(
                                links.editActivityPointsRule(String(rule.id))
                            );
                        }
                    },
                    {
                        label: 'Delete',
                        action: (rule) => {
                            setSelectedRule(rule);
                            setDeleteDialogOpen(true);
                        }
                    }
                ]}
            />

            <DeleteActivityPointsRuleDialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                rule={selectedRule}
                onSuccess={() => {
                    activityPointsRules.request([searchParams, filters]);
                }}
            />
        </>
    );
});
